import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { CRMIntegrationsService } from '../../../services/crm-integrations.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ZintModalConfig } from '../../../classes/types';
import { publicMethods } from '../../../globals';
import { Subscription, timer } from 'rxjs';
import { switchMap, takeUntil, tap } from 'rxjs/operators';

interface DynamicsAuthStatus {
  isReady: boolean;
  connectionId: number | null;
  instanceUrl: string | null;
  environmentName: string | null;
  authorisationPending?: boolean;
}

@Component({
  selector: 'wizard-crm-auth',
  templateUrl: './wizard-crm-auth.component.html',
  styleUrl: './wizard-crm-auth.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class WizardCrmAuthComponent {
  @Input() stepData: Record<string, any>;
  @Output() setStepIsReady = new EventEmitter<boolean>();
  @Output() connectionChanged = new EventEmitter<void>();

  ACTION_TYPE = 'get_auth_url';

  crmType: string;
  requiredCheckboxChecked: boolean;
  authURL: string;

  pollingSub: Subscription;

  allowReauthorisation = false;
  showTermsModal = false;
  showDataUsageModal = false;
  termsModalConfig: ZintModalConfig;
  dataUsageModalConfig: ZintModalConfig;
  isAuthorisingDynamics = false;
  dynamicsEnvironmentName: string;
  dynamicsEnvironmentHost: string;
  dynamicsAuthorisationMessage = '';
  private lastDynamicsAuthStatus: DynamicsAuthStatus;
  private dynamicsAuthRequest: Subscription;
  private dynamicsInitialStatusRequest: Subscription;

  constructor(
    public crmService: CRMIntegrationsService,
    public pubMethods: publicMethods,
    private snackbar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {
    // run effect and emit if step is for non-su
    effect(() => {
      const isAuthReady =
        this.crmService.wizardStepsReadyState()[this.stepData.stepType];
      if (isAuthReady && this.stepData.superuserOnly === false) {
        this.setStepIsReady.emit(isAuthReady);
      }
    });
  }

  ngOnInit(): void {
    this.crmType = this.crmService.configCRMType();
    if (this.crmType === 'dynamics') {
      this.dynamicsInitialStatusRequest = this.crmService
        .getWizardStepStatus(this.stepData.stepType)
        .subscribe(data => {
          if (typeof data?.isReady === 'boolean') {
            this.applyDynamicsStatus(data);
          }
        });
    } else {
      this.crmService.getCRMConfigStepStatus(this.stepData.stepType);
    }
  }

  ngAfterViewInit(): void {
    this.requiredCheckboxChecked = this.stepData.isReady;
    this.allowReauthorisation = this.stepData.isReady;
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.stopPoll();
    this.isAuthorisingDynamics = false;
    this.dynamicsAuthRequest?.unsubscribe();
    this.dynamicsInitialStatusRequest?.unsubscribe();
  }

  startPoll(): void {
    this.stopPoll();
    this.pollingSub = timer(3000, 2500)
      .pipe(
        switchMap(() => {
          // switchMap cancels previous requests if a new one is triggered before the last one is complete.
          return this.crmService.getWizardStepStatus(this.stepData.stepType);
        }),
        tap(data => {
          if (data?.isReady) {
            this.stopPoll();
            this.crmService.wizardStepsReadyState.update(state => {
              return { ...state, [this.stepData.stepType]: data.isReady };
            });
          }
        })
      )
      .subscribe();
  }

  stopPoll(): void {
    this.pollingSub?.unsubscribe();
  }

  getStepActionContent(): void {
    // Dynamics starts a fresh, browser-bound flow when the user clicks Sign in.
    if (this.crmType === 'dynamics') return;
    this.crmService
      .getCRMStepActionContent({
        stepType: this.stepData.stepType,
        actionType: this.ACTION_TYPE,
      })
      .subscribe(data => {
        if (data) {
          this.authURL = data.url;
        }
      });
  }

  authAction(): void {
    if (this.crmType === 'dynamics') {
      this.startDynamicsAuthorisation();
      return;
    }
    if (this.authURL) {
      this.startPoll();
      window.open(this.authURL, '_blank');
    } else {
      this.reAuthAction();
    }
  }

  reAuthAction(): void {
    if (this.crmType === 'dynamics') {
      this.startDynamicsAuthorisation();
      return;
    }
    // Request a new AuthURL then open it immediately
    this.crmService
      .getCRMStepActionContent({
        stepType: this.stepData.stepType,
        actionType: this.ACTION_TYPE,
      })
      .subscribe(data => {
        if (data) {
          this.authURL = data.url;
          if (this.authURL) {
            window.open(this.authURL, '_blank');
          } else {
            this.snackbar.open(
              'We were not able to initiate authorisation. Please refresh the page and try again',
              'X',
              {
                duration: 3100,
              }
            );
          }
        }
      });
  }

  private applyDynamicsStatus(status: DynamicsAuthStatus): void {
    this.lastDynamicsAuthStatus = status;
    this.dynamicsEnvironmentName = status.environmentName;
    try {
      this.dynamicsEnvironmentHost = status.instanceUrl
        ? new URL(status.instanceUrl).hostname
        : null;
    } catch {
      this.dynamicsEnvironmentHost = null;
    }
    this.allowReauthorisation = status.isReady;
    this.crmService.wizardStepsReadyState.update(state => ({
      ...state,
      [this.stepData.stepType]: status.isReady && !this.isAuthorisingDynamics,
    }));
    this.cdr.markForCheck();
  }

  private startDynamicsAuthorisation(): void {
    if (!this.requiredCheckboxChecked || this.isAuthorisingDynamics) return;

    // Open during the click so the browser does not block the asynchronous OAuth URL.
    const authWindow = window.open('', '_blank');
    if (!authWindow) {
      this.dynamicsAuthorisationMessage =
        'Please allow pop-ups for Zint, then sign in again.';
      return;
    }
    authWindow.opener = null;
    this.isAuthorisingDynamics = true;
    this.dynamicsAuthorisationMessage = '';
    this.crmService.wizardStepsReadyState.update(state => ({
      ...state,
      [this.stepData.stepType]: false,
    }));

    this.dynamicsAuthRequest = this.crmService
      .getCRMStepActionContent({
        stepType: this.stepData.stepType,
        actionType: this.ACTION_TYPE,
      })
      .subscribe(data => {
        if (!this.isAuthorisingDynamics) return;
        if (!data?.url || authWindow.closed) {
          authWindow.close();
          this.finishDynamicsAuthorisation(
            'We could not start authorisation. Please sign in again.'
          );
          return;
        }
        authWindow.location.href = data.url;
        this.startDynamicsPoll(data.connectionId ?? null);
      });
  }

  private startDynamicsPoll(previousConnectionId: number | null): void {
    this.stopPoll();
    this.pollingSub = timer(1000, 2500)
      .pipe(
        switchMap(() =>
          this.crmService.getWizardStepStatus(this.stepData.stepType)
        ),
        takeUntil(timer(10 * 60 * 1000))
      )
      .subscribe({
        next: data => {
          if (typeof data?.isReady !== 'boolean') {
            this.finishDynamicsAuthorisation(
              'We could not check your connection. Refresh this page to check it before trying again.'
            );
            return;
          }
          this.applyDynamicsStatus(data);
          if (
            data.isReady &&
            data.connectionId != null &&
            data.connectionId !== previousConnectionId
          ) {
            this.finishDynamicsAuthorisation();
            this.requiredCheckboxChecked = true;
            this.crmService.cachedCRMObjectFields$ = null;
            this.connectionChanged.emit();
          } else if (data.authorisationPending === false) {
            this.finishDynamicsAuthorisation(
              'Authorisation was not completed. You can sign in again to retry.'
            );
          }
        },
        complete: () =>
          this.finishDynamicsAuthorisation(
            'We could not confirm authorisation. Refresh this page to check your connection, or sign in again.'
          ),
      });
  }

  retryDynamicsAuthorisation(): void {
    this.dynamicsAuthRequest?.unsubscribe();
    this.finishDynamicsAuthorisation();
    this.startDynamicsAuthorisation();
  }

  private finishDynamicsAuthorisation(message = ''): void {
    this.stopPoll();
    this.isAuthorisingDynamics = false;
    this.dynamicsAuthorisationMessage = message;
    if (this.lastDynamicsAuthStatus) {
      this.applyDynamicsStatus(this.lastDynamicsAuthStatus);
    } else {
      this.crmService.wizardStepsReadyState.update(state => ({
        ...state,
        [this.stepData.stepType]: this.stepData.isReady,
      }));
    }
    this.cdr.markForCheck();
  }

  isTnCCheckboxDisabled(): boolean {
    const { isCompleted, isReady, superuserOnly } = this.stepData;
    return superuserOnly ? isCompleted : isReady;
  }

  openTermsModal(): void {
    this.termsModalConfig = {
      showHeaderRow: false,
      size: 'xl',
      name: 'crmTerms',
    };

    this.showTermsModal = true;
    this.cdr.detectChanges();
    this.pubMethods.triggerZintModal(this.termsModalConfig.name);
  }

  openDataUseModal(): void {
    this.dataUsageModalConfig = {
      showHeaderRow: false,
      size: 'xl',
      name: 'dataUsage',
    };

    this.showDataUsageModal = true;
    this.cdr.detectChanges();
    this.pubMethods.triggerZintModal(this.dataUsageModalConfig.name);
  }
}
